import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import axiosInstance from '../api/axiosInstance';

// Cache configuration constant
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

// Helper to safely check if we're in development mode
// Note: process.env.NODE_ENV is injected by webpack DefinePlugin at build time
const isDevelopment = (): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - process is defined by webpack DefinePlugin at build time
    return process.env.NODE_ENV !== 'production';
  } catch {
    return true; // Default to development if process is not defined
  }
};

export interface TenantResponseDto {
  id: string;
  name: string;
  subdomain?: string;
  domain?: string;
  entraTenantId?: string;
  subscriptionTier: string;
  isActive: boolean;
  country?: string;
  industry?: string;
  maxUsers?: number;
  maxProjects?: number;
  userCount?: number;
  activeUserCount?: number;
  pendingInvitationsCount?: number;
  totalProjectCount?: number;
  createdDate: string;
  modifiedDate?: string | null;
  [key: string]: unknown;
}

export interface PaginatedTenantResponse {
  items: TenantResponseDto[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export interface TenantFetchParams {
  pageNumber?: number;
  pageSize?: number;
  name?: string;
  status?: 'Active' | 'Inactive';
  subscriptionTier?: string;
  showExternal?: boolean;
  country?: string;
  industry?: string;
  sortBy?: string;
  sortOrder?: 'Asc' | 'Desc';
}

interface TenantState {
  // Cached data
  tenants: TenantResponseDto[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;

  // Loading states
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;

  // Cache metadata
  lastFetchTime: number | null;
  lastFetchParams: TenantFetchParams | null;

  // Actions
  fetchTenants: (params?: TenantFetchParams, forceRefresh?: boolean) => Promise<void>;
  clearCache: () => void;
  invalidateCache: () => void;
  getTenantById: (id: string) => TenantResponseDto | undefined;
}

export const useTenantStore = create<TenantState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      tenants: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: 1,
      pageSize: 20,
      isLoading: false,
      isFetching: false,
      error: null,
      lastFetchTime: null,
      lastFetchParams: null,

      fetchTenants: async (params: TenantFetchParams = {}, forceRefresh = false) => {
        const state = get();
        const now = Date.now();

        // Check if cache is valid and params match
        const cacheValid =
          state.lastFetchTime && now - state.lastFetchTime < CACHE_DURATION_MS && !forceRefresh;

        const paramsMatch =
          state.lastFetchParams && JSON.stringify(state.lastFetchParams) === JSON.stringify(params);

        // Return cached data if valid
        if (cacheValid && paramsMatch && state.tenants.length > 0) {
          return;
        }

        // Set loading state
        set(
          {
            isFetching: true,
            isLoading: state.tenants.length === 0, // Only show skeleton on first load
            error: null,
          },
          false,
          'fetchTenants/start'
        );

        try {
          const urlParams = new URLSearchParams();

          urlParams.append('PageNumber', (params.pageNumber || 1).toString());
          urlParams.append('PageSize', (params.pageSize || 20).toString());

          if (params.name) urlParams.append('Name', params.name);
          if (params.status) urlParams.append('Status', params.status);
          if (params.subscriptionTier && params.subscriptionTier !== 'All') {
            urlParams.append('SubscriptionTier', params.subscriptionTier);
          }
          if (params.showExternal !== undefined) {
            urlParams.append('ShowExternal', params.showExternal.toString());
          }
          if (params.country && params.country !== 'All') {
            urlParams.append('Country', params.country);
          }
          if (params.industry && params.industry !== 'All') {
            urlParams.append('Industry', params.industry);
          }
          if (params.sortBy) urlParams.append('SortBy', params.sortBy);
          if (params.sortOrder) urlParams.append('SortOrder', params.sortOrder);

          const response = await axiosInstance.get<PaginatedTenantResponse>(
            `/api/tenants?${urlParams.toString()}`
          );

          set(
            {
              tenants: response.data.items,
              totalCount: response.data.totalCount,
              totalPages: response.data.totalPages,
              currentPage: response.data.currentPage,
              pageSize: response.data.pageSize,
              isLoading: false,
              isFetching: false,
              error: null,
              lastFetchTime: now,
              lastFetchParams: params,
            },
            false,
            'fetchTenants/success'
          );
        } catch (error: unknown) {
          console.error('Error fetching tenants:', error);
          set(
            {
              isLoading: false,
              isFetching: false,
              error: error instanceof Error ? error.message : 'Failed to fetch tenants',
            },
            false,
            'fetchTenants/error'
          );
        }
      },

      clearCache: () => {
        set(
          {
            tenants: [],
            totalCount: 0,
            totalPages: 0,
            currentPage: 1,
            pageSize: 20,
            lastFetchTime: null,
            lastFetchParams: null,
            error: null,
          },
          false,
          'clearCache'
        );
      },

      invalidateCache: () => {
        set(
          {
            lastFetchTime: null,
            lastFetchParams: null,
          },
          false,
          'invalidateCache'
        );
      },

      getTenantById: (id: string) => get().tenants.find((tenant) => tenant.id === id),
    })),
    {
      name: 'Helios Tenant Store',
      // Enable devtools in development, disable in production
      enabled: isDevelopment(),
    }
  )
);
